import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { StayQuote, StayQuoteQuery, TripBasketLine, TripStopOffer } from "@majourneys/discover";
import type { PackageInfo } from "@majourneys/booking";
import { activitiesApi } from "../../activities/api/activities.api";
import { staysApi } from "../../stays/api/stays.api";
import { useCurrency } from "../../../lib/currency";
import { useGuestNationality } from "../../../lib/guest-nationality";
import { formatStayMoney } from "../../stays/lib/money";
import { useBucketOptional } from "../../bucket/state/bucket-context";
import type { BucketAddInput } from "../../bucket/state/bucket-types";

interface DetailShape {
  id?: string;
  slug?: string;
  title?: string;
  cityName?: string;
  images?: string[];
  template?: string | null;
  packages?: PackageInfo[];
  infantMaxAge?: number | null;
  childrenMaxAge?: number | null;
}

/** The activity detail the page itself loads, cut down to what a trip books from. */
export function offerFromDetail(raw: unknown, slug: string): TripStopOffer | null {
  const body = raw as { data?: DetailShape } | DetailShape | null | undefined;
  const detail = (body && "data" in body ? body.data : body) as DetailShape | null | undefined;
  if (!detail?.id || !Array.isArray(detail.packages)) return null;
  return {
    activityId: detail.id,
    slug: detail.slug ?? slug,
    title: detail.title ?? slug,
    cityName: detail.cityName ?? "",
    thumbnail: detail.images?.[0] ?? null,
    template: detail.template ?? null,
    packages: detail.packages,
    // The venue's own age bands, so a trip places children as its page does.
    ...(detail.infantMaxAge != null ? { infantMaxAge: detail.infantMaxAge } : {}),
    ...(detail.childrenMaxAge != null ? { childrenMaxAge: detail.childrenMaxAge } : {}),
  };
}

/** A trip's bucket line, as the bucket takes it. Same fields, one to one. */
export function lineToBucketInput(line: TripBasketLine): BucketAddInput {
  return {
    activityId: line.activityId,
    activitySlug: line.activitySlug,
    packageId: line.packageId,
    date: line.date,
    time: line.time,
    adults: line.adults,
    children: line.children,
    infants: line.infants,
    hours: line.hours,
    groups: line.groups,
    transport: line.transport,
    transportOutside: line.transportOutside,
    thumbnail: line.thumbnail,
    title: line.title,
    cityName: line.cityName,
    typePrice: line.typePrice,
    maxGuests: line.maxGuests,
    includedGuests: line.includedGuests,
    pricing: line.pricing,
    priceSnapshot: line.priceSnapshot,
  };
}

/**
 * The trip planner's way into the bucket (`DiscoverConfig.useTripBooking`).
 *
 * Offers come from the same activity endpoint the activity page reads, and
 * lines go through the same `bucket.add` the booking sheet uses, so a signed-in
 * guest's lines are re-priced by the server exactly as any other. Null where
 * there is no bucket (a test tree), which hides the booking button.
 */
export function useTripBooking() {
  const bucket = useBucketOptional();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "en";
  // Priced as the hotel page prices it: the guest's currency and nationality,
  // so the plan and the page show one number.
  const { currency } = useCurrency();
  const guestNationality = useGuestNationality();

  return useMemo(() => {
    if (!bucket) return null;
    return {
      fetchOffer: async (slug: string, offerLang: string) =>
        offerFromDetail(await activitiesApi.getBySlug(slug, offerLang), slug),
      addToBucket: async (lines: readonly TripBasketLine[]) => {
        let added = 0;
        // One at a time, so a signed-in guest's lines reach the server (which
        // re-prices each one) in the order of the day.
        for (const line of lines) {
          const result = await bucket.add(lineToBucketInput(line));
          if (result) added += 1;
        }
        return added;
      },
      openBucket: () => {
        void navigate({ href: `/${lang}/bucket` });
      },
      fetchStayQuote: async (q: StayQuoteQuery): Promise<StayQuote | null> => {
        const res = await staysApi.property(q.slug, q.lang, {
          checkin: q.checkin,
          checkout: q.checkout,
          adults: q.adults,
          childAges: [...q.childAges],
          currency,
          guestNationality,
        });
        // The cheapest room for those nights and that party: the "from" the
        // hotel page opens on.
        const cheapest = res.rooms.reduce<(typeof res.rooms)[number] | null>(
          (best, r) => (best === null || r.sellTotal < best.sellTotal ? r : best),
          null,
        );
        return cheapest ? { amount: Math.round(cheapest.sellTotal), currency: cheapest.currency } : null;
      },
      formatStayAmount: (amount: number, cur: string) => formatStayMoney(amount, cur, lang),
    };
  }, [bucket, navigate, lang, currency, guestNationality]);
}
